import { useEffect, useRef, useState } from 'react';
import {Controller, useForm} from 'react-hook-form';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Grid,
    TextField,
    Typography,
    Button,
    IconButton,
    FormHelperText,
    MenuItem,
} from '@mui/material';
import { Delete, InsertDriveFile, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { useCommonItems } from '../../../../../../contexts/CommonItensProvider';
import { CommonDialogActionsForm } from '../../../../../common/CommonDialogActionsForm';
import { updateTutorial, createTutorial } from '../configs/functions';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx'];

const getErrorMessage = (errors, name) => errors?.[name]?.message ?? '';

export function TutorialForm({
    open,
    setOpen,
    tutorial,
    actionsAllowed = true,
    onSuccess,
}) {
    const { exibirAlerta } = useCommonItems();

    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const isEdit = Boolean(tutorial?.id);
    const tutorialStatus = Boolean(tutorial?.status)

    const {
        register,
        handleSubmit,
        reset,
        control,
        setError,
        clearErrors,
        watch,      
        formState: { errors },
    } = useForm({
        mode: 'onChange',
        defaultValues: { nome: '', tipo_arquivo: '', descricao: '', tipo: ''},
    });

 const tipoSelecionado = watch('tipo');
const isDuvida = tipoSelecionado === 'duvida';
    useEffect(() => {
        if (!open) {
            reset();
            setSelectedFile(null);
            setFileError('');
            setIsDragging(false);
            return;
        }

        if (tutorial?.id) {
            reset({
                nome: tutorial.nome ?? '',
                tipo_arquivo : tutorial.tipoArquivo,
                tipo: tutorial.tipo,
                descricao: tutorial.descricao ?? '',
                arquivo : tutorial.arquivo ?? '',
                status : tutorial.status ??  ''
            });
        } else {
            reset({
                nome: '',
                tipo_arquivo: '',
                tipo: '',
                descricao: '',
                arquivo: '',
                status: ''
            });
        }
    }, [open, tutorial, reset]);

    useEffect(() => {
    if (isDuvida) {
        setSelectedFile(null);
        setFileError('');
        setIsDragging(false);
    }
}, [isDuvida]);

    function selectFile(file) {
        if (!file) return;

        const ext = file.name.split('.').pop()?.toLowerCase();

        if (!ACCEPTED_EXTENSIONS.includes(ext)) {
            setFileError('Formato inválido. Use PDF, DOC ou DOCX.');
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setFileError('O arquivo deve ter no máximo 5 MB');
            return;
        }

        setFileError('');
        setSelectedFile(file);
        clearErrors('arquivo');
    }

    const handleFileChange = (e) => {
        selectFile(e.target.files?.[0]);
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (isDuvida) return;
        setIsDragging(true);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (isDuvida) return;
        selectFile(e.dataTransfer.files?.[0]);
    };
    const handleDragLeave = () => setIsDragging(false);

    const handleRemoveFile = (e) => {
        e.stopPropagation();
        setSelectedFile(null);
        setFileError('');
    };

const onSubmit = async (data) => {
        if (!isEdit && !isDuvida && !selectedFile) {
            setFileError('Informe o arquivo do tutorial');
            return;
        }
    if (fileError) return;
    setIsLoading(true);
    try {
        const formData = new FormData();
        formData.append('nome', data.nome);
        formData.append('tipo_arquivo', data.tipo_arquivo);
        formData.append('descricao', data.descricao ?? '');
        formData.append('tipo',data.tipo ?? '')
        formData.append('status',1);
        if (selectedFile) {
            formData.append('arquivo', selectedFile);
        }
        if (isEdit) {
            await updateTutorial(tutorial.id, {'nome': data.nome, tipo_arquivo: data.tipo_arquivo,'descricao': data.descricao ?? '', 'status': tutorialStatus ?? 1, tipo: data.tipo});
        } else {
            await createTutorial(formData);
        }

        setOpen(false);
        exibirAlerta(
            'Sucesso',
            isEdit ? 'Tutorial atualizado com sucesso' : 'Tutorial cadastrado com sucesso',
            'success'
        );
        onSuccess?.();
    } catch (error) {
        exibirAlerta('Erro', 'Não foi possível salvar o tutorial', 'error');
    } finally {
        setIsLoading(false);
    }
};

    return (
        <Dialog
            open={open}
            onClose={() => !isLoading && setOpen(false)}
            fullWidth
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    {isEdit ? 'Editar Tutorial' : 'Cadastrar Tutorial'}
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome do Tutorial/Pergunta"
                                    placeholder="Digite o nome do tutorial"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('nome', {
                                        required: 'Informe o nome do tutorial',
                                    })}
                                    error={!!errors.nome}
                                    helperText={getErrorMessage(errors, 'nome')}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Controller
                                    name="tipo_arquivo"
                                    control={control}
                                    rules={{ required: 'Informe a visibilidade do arquivo' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            value={field.value ?? ''}
                                            select
                                            fullWidth
                                            label="Visibilidade do arquivo"
                                            InputLabelProps={{ shrink: true }}
                                            SelectProps={{
                                                displayEmpty: true,
                                                renderValue: (value) =>
                                                    value ? (
                                                        value === 'interno' ? 'Interno' : 'Externo'
                                                    ) : (
                                                        <span style={{ color: '#9e9e9e' }}>Selecione a visibilidade do arquivo</span>
                                                    ),
                                            }}
                                            error={!!errors.tipo_arquivo}
                                            helperText={getErrorMessage(errors, 'tipo_arquivo')}
                                        >
                                            <MenuItem value="interno">Interno</MenuItem>
                                            <MenuItem value="externo">Externo</MenuItem>
                                        </TextField>
                                    )}
                                />
                            </Grid>


                            <Grid item xs={12}>
                                <Controller
                                    name="tipo"
                                    control={control}
                                    rules={{ required: 'Informe o tipo do arquivo' }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            value={field.value ?? ''}
                                            select
                                            fullWidth
                                            label="Tipo do arquivo"
                                            InputLabelProps={{ shrink: true }}
                                            SelectProps={{
                                                displayEmpty: true,
                                                renderValue: (value) =>
                                                    value ? (
                                                        value === 'duvida' ? 'Duvida' : 'Tutorial'
                                                    ) : (
                                                        <span style={{ color: '#9e9e9e' }}>Selecione o tipo do arquivo</span>
                                                    ),
                                            }}
                                            error={!!errors.tipo}
                                            helperText={getErrorMessage(errors, 'tipo')}
                                        >
                                        <MenuItem value="duvida">Dúvida (Resposta curta em texto)</MenuItem>
                                        <MenuItem value="tutorial">Tutorial (Arquivo)</MenuItem>
                                        </TextField>
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label={isDuvida ? 'Resposta da pergunta' : 'Descrição (opcional)'}
                                    placeholder={isDuvida ? 'Digite a resposta da pergunta' : 'Digite a descrição'}
                                    InputLabelProps={{ shrink: true }}
                                    {...register('descricao', {
                                        required: isDuvida ? 'Informe a resposta da pergunta' : false,
                                    })}
                                    error={!!errors.descricao}
                                    helperText={getErrorMessage(errors, 'descricao')}
                                />
                            </Grid>

                        {!isEdit && (
                            <Grid item xs={12}>
                            <Box
                                onClick={() => !isDuvida && fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                sx={{
                                    border: '2px dashed',
                                    borderColor: fileError
                                        ? 'error.main'
                                        : isDragging
                                            ? 'primary.main'
                                            : 'grey.400',
                                    borderRadius: 2,
                                    p: 4,
                                    textAlign: 'center',
                                    cursor: isDuvida ? 'not-allowed' : 'pointer',
                                    position: 'relative',
                                    opacity: isDuvida ? 0.5 : 1,
                                    pointerEvents: isDuvida ? 'none' : 'auto',
                                    backgroundColor: isDragging || selectedFile ? 'action.hover' : 'background.paper',
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: isDuvida ? 'grey.400' : 'primary.main' },
                                }}
                            >
                                    {selectedFile && (
                                        <IconButton
                                            onClick={handleRemoveFile}
                                            sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.600' }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    )}

                                    {selectedFile ? (
                                        <>
                                            <InsertDriveFile color="primary" sx={{ fontSize: 40 }} />
                                            <Typography variant="body1" sx={{ mt: 1, wordBreak: 'break-word' }}>
                                                {selectedFile.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                            </Typography>
                                            <Button variant="outlined">Alterar Arquivo</Button>
                                        </>
                                    ) : (
                                        <>
                                            <UploadFileIcon color="primary" sx={{ fontSize: 40 }} />
                                            <Typography variant="body1" sx={{ mt: 1 }}>
                                                Arraste ou clique para adicionar o arquivo
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                {isEdit && tutorial?.arquivo
                                                    ? `Arquivo atual: ${tutorial.arquivo} • PDF, DOC ou DOCX até 5 MB`
                                                    : 'PDF, DOC ou DOCX até 5 MB'}
                                            </Typography>
                                            <Button variant="contained">Selecionar Arquivo</Button>
                                        </>
                                    )}

                                    <input
                                        type="file"
                                        hidden
                                        disabled={isDuvida}
                                        accept=".pdf,.doc,.docx"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                    />
                                </Box>

                                {fileError && <FormHelperText error>{fileError}</FormHelperText>}
                            </Grid>
                            )}
                        </Grid>
                    </Box>
                </DialogContent>

                <CommonDialogActionsForm
                    errors={errors}
                    isLoading={isLoading}
                    setOpen={setOpen}
                />
            </form>
        </Dialog>
    );
}